import * as R from "ramda";
import React from "react";
import { FaPlus, FaTrash } from "react-icons/fa";
import { connect } from "react-redux";
import { compose, Dispatch } from "redux";
import { IAction } from "reduxsauce";
import styled from "styled-components";
import { IStyle } from "../../../Types/Style";
import { IClientPatch, IPatchAction } from "../../../components/course-author/helpers/patches";
import { patchPayloadCreator } from "../../../components/course-author/helpers/patches/creators";
import { config } from "../../../config";
import { accountId as currentAccountId } from "../../../selectors/selectors";
import { FieldSet, FooterNote, Input, InputLink } from "../../Common/Components/FormComponents";
import {
  allowedTypes,
  allowedTypesFormats,
  BackgroundPreview,
  ButtonsRow,
  DropDownImage,
  DropDownMessage,
  ErrorMsg,
  FixedAddButton,
  FixedDeleteButton,
  HandleImageContainer,
  PreviewImageContainer,
} from "../../Common/Styles/DNDImage";
import { IState } from "../../reducer";
import * as actions from "../actions";
import { Section } from "../components/common/containers";
import { modifyLogoPatch } from "../helpers/patches";
import Dropzone from "react-dropzone";

const DeleteButton = styled(FixedDeleteButton)`
  @media (${({ theme }) => theme.breakpoints.min800}) {
    width: 14rem;
  }
`;
const FirstSection = styled(Section)`
  margin: 0 0 1.5rem 0;
`;
interface IYourLogoOwnProps {
  style: IStyle;
}
interface IYourLogoStateProps {
  accountId: string;
  addNewLogoError: string | null;
}

interface IYourLogoDispatchProps {
  addLogo: (files: File[] | null, link: string | null) => void;
  deleteLogo: (versionId: number, patch: IClientPatch<IPatchAction>) => void;
}

type YourLogoProps = IYourLogoOwnProps & IYourLogoStateProps & IYourLogoDispatchProps;

const mkImageUrl = (imgId: string): string => `${config.saltcomplianceApiRoot}/images/${imgId}/_download`;

export const YourLogo: React.FC<YourLogoProps> = props => {
  const linkInputName = "logo-img-url";
  const fileInputName = "logo-file-upload";
  const [link, setLink] = React.useState<string | null>(null);
  const { accountId, addNewLogoError, style } = props;
  const canDeleteImage = !!style.logoImageId;

  const onDeleteFile = () => {
    if (canDeleteImage && window.confirm("Are you sure you want to delete the current logo?")) {
      props.deleteLogo(style.versionId, modifyLogoPatch(style, accountId, null));
    }
  };

  const onLinkChange = (e: React.ChangeEvent<HTMLInputElement>) => setLink(e.target.value);

  const uploadFile = () => (files: File[] | undefined) => {
    if (!files) return;

    if (files.length > 1) {
      alert("Make sure to upload only one image");
      return;
    }

    if (!R.isNil(files) && files.length > 0 && !allowedTypes.includes(files[0].type)) {
      alert(`File type must be one of these: ${allowedTypesFormats}`);
      return;
    }
    if (!R.isNil(files) || !R.isNil(link)) {
      props.addLogo(files, link);
    }
  };

  const handleOnDrop = (files: File[]) => uploadFile()(files);

  return (
    <FirstSection>
      <h2>Your Logo(s)</h2>
      <HandleImageContainer>
        <Dropzone onDrop={handleOnDrop}>
          {({ getRootProps, getInputProps }) => (
            <div style={{ width: "18rem" }} {...getRootProps()}>
              <DropDownImage>
                <DropDownMessage>Drop image file here or enter URL below</DropDownMessage>
              </DropDownImage>
              <Input
                {...getInputProps()}
                data-testid="image-uploader"
                name={fileInputName}
                type="file"
                style={{ display: "none" }}
              />
            </div>
          )}
        </Dropzone>
        {!!style.logoImageId && (
          <PreviewImageContainer>
            <BackgroundPreview alt="Logo Image Preview" src={mkImageUrl(style.logoImageId)} />
          </PreviewImageContainer>
        )}
      </HandleImageContainer>
      <FieldSet>
        <legend>Choose logo url</legend>
        <InputLink name={linkInputName} onChange={onLinkChange} placeholder={"Type or paste here"} />
      </FieldSet>
      {addNewLogoError && <ErrorMsg>{addNewLogoError}</ErrorMsg>}
      <ButtonsRow width={28} margin={1}>
        <FixedAddButton onClick={uploadFile}>
          <FaPlus /> Add
        </FixedAddButton>
        <DeleteButton onClick={onDeleteFile} disabled={!canDeleteImage}>
          <FaTrash /> Delete logo
        </DeleteButton>
      </ButtonsRow>
      <FooterNote>* Supported files: {allowedTypesFormats}</FooterNote>
    </FirstSection>
  );
};
export default compose(
  connect(
    (state: IState): IYourLogoStateProps => ({
      accountId: currentAccountId(state),
      addNewLogoError: state.App.ThemeManagement.addNewLogoError?.message ?? null,
    }),
    (dispatch: Dispatch<IAction>, ownProps: IYourLogoOwnProps): IYourLogoDispatchProps => ({
      addLogo: (files: File[] | null, link: string | null) =>
        dispatch(
          actions.addNewLogo({
            files,
            link,
            style: ownProps.style,
          })
        ),
      deleteLogo: (versionId: number, patch: IClientPatch<IPatchAction>) =>
        dispatch(
          actions.patchStyle({
            location: "Logo",
            patch: patchPayloadCreator(versionId, patch),
          })
        ),
    })
  )
)(YourLogo);
